﻿import { IMsalContext, useMsal } from "@azure/msal-react";
import { createContext, FunctionComponent, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IEnvironmentContext, useEnvironmentContext } from "../EnvironmentContext";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { IconExclamationCircle, IconPlugConnectedX } from "@tabler/icons-react";
import { UseToastOptions } from "@chakra-ui/react";
import { useToast } from "../../hooks";
import { PageLoading } from "../../components";

export interface IApiContext {
    accessToken: string | undefined;
    idToken: string | undefined;
}

const defaultState: IApiContext = {
    accessToken: undefined,
    idToken: undefined,
};

const ApiContext = createContext<IApiContext>(defaultState);

async function acquireAccessToken(envConfig: IEnvironmentContext, msalContext: IMsalContext) {
    const accessTokenRequest = {
        scopes: envConfig.authScopes,
        account: msalContext.instance.getAllAccounts()[0],
    };

    try {
        return await msalContext.instance.acquireTokenSilent(accessTokenRequest);
    } catch (error) {
        return await msalContext.instance.acquireTokenPopup(accessTokenRequest);
    }
}

function getServerDownMessage<TError>(error: TError) {
    // @ts-ignore
    if (error.message === "Failed to fetch") {
        return "Unable to connect to the server. Please try again later.";
    }
}

export type ApiContextProviderProps = PropsWithChildren<{
    onApiContextInit?: (baseUrl: string, getAccessToken: () => Promise<string>) => void;
}>;

/**
 * This will toast an error message to the user in a uniform way. If the error is a 401 or 403, the user will be redirected to the unauthorized page.
 * @param toast
 * @param error
 * @param defaultMessage
 * @param message
 */
export const handleError = (
    toast: (options?: UseToastOptions) => void,
    error: unknown,
    defaultMessage = "An unexpected error occurred",
    message?: string | undefined,
) => {
    const serverDownMessage = getServerDownMessage(error);
    let errorMessage = defaultMessage;
    if (message) {
        errorMessage = message;
    }
    if (serverDownMessage) {
        errorMessage = serverDownMessage;
    }

    toast({
        title: "Error",
        description: `${errorMessage}`,
        status: "error",
        isClosable: true,
        duration: 6000,
        icon: serverDownMessage ? <IconPlugConnectedX /> : <IconExclamationCircle />,
    });

    // @ts-ignore
    if (error?.status === 401 || error?.status === 403) {
        // navigate("/unauthorized");
    }
};

export function mutationErrorHandler(toast: (options?: UseToastOptions) => void, message: string) {
    return {
        onError: (error: unknown) => {
            handleError(toast, error, message);
        },
    };
}

export function errorMessage(message: string) {
    return {
        meta: {
            errorMessage: message,
        },
    };
}

export const ApiContextProvider: FunctionComponent<ApiContextProviderProps> = ({ onApiContextInit, children }) => {
    const [accessToken, setAccessToken] = useState<string>();
    const [initialized, setInitialized] = useState(false);

    const msalContext = useMsal();
    const envConfig = useEnvironmentContext();
    const toast = useToast();

    const getAccessToken = useCallback(async () => {
        const result = await acquireAccessToken(envConfig, msalContext);

        if (result.accessToken != accessToken) {
            setAccessToken(result.accessToken);
        }
        return result.accessToken;
    }, [accessToken, envConfig, msalContext]);

    useEffect(() => {
        if (onApiContextInit && !initialized) {
            onApiContextInit(envConfig?.apiUrl || "", getAccessToken);
            setInitialized(true);
        }
    }, [onApiContextInit, envConfig?.apiUrl, getAccessToken]); // initialized intentionally left out

    const contextValue = {
        accessToken,
        idToken: msalContext.instance.getAllAccounts()[0]?.idToken,
    };

    const queryClient = useMemo(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        retry: 0,
                        cacheTime: 30 * 60 * 1000,
                        staleTime: 15 * 60 * 1000,
                        refetchOnWindowFocus: false,
                        refetchOnMount: false,
                        refetchOnReconnect: false,
                    },
                    mutations: {
                        ...mutationErrorHandler(toast, "Unable to perform operation. Please try again."),
                    },
                },
                queryCache: new QueryCache({
                    onError: (error, query) => {
                        let errorMessage;
                        if (query?.meta?.errorMessage) {
                            errorMessage = query.meta.errorMessage as string;
                        }

                        handleError(toast, error, "Unable to load data. Please try again.", errorMessage);
                    },
                }),
            }),
        [], // intentionally empty so that the queryClient is only created once... this could potentially be dangerous we will need to monitor this
    );

    return initialized ? (
        <QueryClientProvider client={queryClient}>
            <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>
        </QueryClientProvider>
    ) : (
        <PageLoading text={"Initializing API Client..."} />
    );
};

export const useApiContext = () => useContext(ApiContext);
